import { useCallback, useMemo, useState } from "react";

import {
  PAGE_SIZE_OPTIONS,
  PageSizeSchema,
  type PageSize,
  type PaginationState,
} from "../schemas";

const DEFAULT_THRESHOLD = 5;
const ELLIPSIS = "ellipsis";
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

interface UsePaginationProps {
  initialPageSize?: PageSize;
  initialPageIndex?: number;
  threshold?: number;
}

interface UsePaginationReturn {
  pagination: PaginationState;
  setPagination: (
    updater: PaginationState | ((old: PaginationState) => PaginationState)
  ) => void;
  resetToFirstPage: () => void;
  getPageNumbers: (currentPage: number, totalPages: number) => PageNumber[];
}

type PageNumber = number | typeof ELLIPSIS;

function generatePageNumbers(
  currentPage: number,
  totalPages: number,
  threshold = DEFAULT_THRESHOLD
): PageNumber[] {
  if (totalPages <= 1) return [1];

  if (totalPages <= threshold) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const reservedSlots = 2;
  const availableSlots = Math.max(1, threshold - reservedSlots);

  const sidePages = Math.floor((availableSlots - 1) / 2);

  let startPage = Math.max(2, currentPage - sidePages);
  let endPage = Math.min(totalPages - 1, currentPage + sidePages);

  if (startPage === 2 && endPage < totalPages - 1) {
    endPage = Math.min(totalPages - 1, startPage + availableSlots - 1);
  } else if (endPage === totalPages - 1 && startPage > 2) {
    startPage = Math.max(2, endPage - availableSlots + 1);
  }

  const result: (number | typeof ELLIPSIS)[] = [1];

  if (startPage > 2) {
    if (startPage === 3 && result.length < threshold - 1) {
      result.push(2);
    } else {
      result.push(ELLIPSIS);
    }
  }

  for (let i = startPage; i <= endPage; i++) {
    result.push(i);
  }

  if (endPage < totalPages - 1) {
    if (endPage === totalPages - 2 && result.length < threshold - 1) {
      result.push(totalPages - 1);
    } else {
      result.push(ELLIPSIS);
    }
  }

  if (totalPages > 1) {
    result.push(totalPages);
  }

  return result;
}

function usePagination({
  initialPageSize = DEFAULT_PAGE_SIZE,
  initialPageIndex = 0,
  threshold = DEFAULT_THRESHOLD,
}: UsePaginationProps = {}): UsePaginationReturn {
  const validatedPageSize = PageSizeSchema.parse(initialPageSize);

  const [pagination, setPaginationState] = useState<PaginationState>({
    pageIndex: initialPageIndex,
    pageSize: validatedPageSize,
  });

  const setPagination = useCallback(
    (
      updater: PaginationState | ((old: PaginationState) => PaginationState)
    ) => {
      setPaginationState(updater);
    },
    []
  );

  const resetToFirstPage = useCallback(() => {
    setPaginationState((prev) => ({ ...prev, pageIndex: 0 }));
  }, []);

  const getPageNumbers = useCallback(
    (currentPage: number, totalPages: number) => {
      return generatePageNumbers(currentPage, totalPages, threshold);
    },
    [threshold]
  );

  return useMemo(
    () => ({
      pagination,
      setPagination,
      resetToFirstPage,
      getPageNumbers,
    }),
    [pagination, setPagination, resetToFirstPage, getPageNumbers]
  );
}

export { usePagination, generatePageNumbers, ELLIPSIS };
export type { UsePaginationProps, UsePaginationReturn, PageNumber };
